import { DownloadOutlined } from '@ant-design/icons'
import { useIntl } from '@umijs/max'
import { Button, message } from 'antd'
import ExcelJS from 'exceljs'
import { saveAs } from 'file-saver'
import { useState } from 'react'

type PaperSize = 'A4' | 'A3' | 'A5' | 'Letter' | 'Legal'

type Props = {
  columns: any[]
  onFinish: () => any[] | Promise<any[]>
  filterColumns?: string[]
  customHeaders?: Record<string, string>
  photoList?: string[]
  paperSize?: PaperSize
  customConfig?: {
    rowTypeField?: string // 行类型字段名
    levelField?: string // 层级字段名
    titleField?: string // 标题字段名
  }
  fileName?: string
}

const ExportExcelButton = ({
  columns,
  onFinish,
  filterColumns = [],
  customHeaders = {},
  photoList = [],
  paperSize = 'A4',
  customConfig = {},
  fileName = '导出数据',
}: Props) => {
  const intl = useIntl()
  const [exporting, setExporting] = useState(false)

  // 纸张大小配置
  const paperSizeConfig: Record<PaperSize, number> = {
    A4: 595,
    A3: 842,
    A5: 420,
    Letter: 612,
    Legal: 612,
  }

  // 获取列标题文本 - 安全地处理不同类型的 title
  const getColumnTitle = (col: any): string => {
    // 如果已经有自定义表头，直接返回
    if (customHeaders[col.dataIndex]) {
      return customHeaders[col.dataIndex]
    }

    // 如果 title 是字符串，直接返回
    if (typeof col.title === 'string') {
      return col.title
    }

    // 如果 title 是 FormattedMessage 组件，获取其 id 并进行国际化
    if (col.title && col.title.props && col.title.props.id) {
      return intl.formatMessage({ id: col.title.props.id })
    }

    // 其他情况，返回数据索引作为备选
    return col.dataIndex || '未知列'
  }

  // 从 React 元素中提取文本内容
  const getTextFromReactNode = (node: any): string => {
    if (node == null) return ''
    if (typeof node === 'string' || typeof node === 'number') return String(node)
    if (Array.isArray(node)) return node.map(getTextFromReactNode).join('')

    // 如果是 React 元素，递归提取 children
    if (node.props && node.props.children) {
      return getTextFromReactNode(node.props.children)
    }

    return String(node)
  }

  // 执行 render 函数获取显示值
  const getRenderValue = (col: any, item: any, index: number): string => {
    if (!col.render) return ''

    try {
      const result = col.render(undefined, item, index, {})
      return getTextFromReactNode(result)
    } catch (error) {
      console.warn(`执行 render 函数失败 (${col.dataIndex}):`, error)
      return ''
    }
  }

  // 获取显示的标题文本（处理树形数据）
  const getDisplayTitle = (item: any, col: any): string => {
    // 优先使用自定义标题字段
    if (customConfig.titleField && item[customConfig.titleField]) {
      return item[customConfig.titleField]
    }

    // 如果有缩进需求，根据层级添加缩进
    if (customConfig.levelField !== undefined && item[customConfig.levelField] !== undefined) {
      const level = item[customConfig.levelField] || 0
      const indent = '  '.repeat(level) // 每级缩进2个空格

      // 如果是运营商行，使用运营商名称
      if (customConfig.rowTypeField && item[customConfig.rowTypeField] === 'operator') {
        return indent + (item.title || item.productTitle || '')
      }

      // 产品行使用产品名称
      return indent + (item.title || '')
    }

    // 默认返回标题
    return item.title || ''
  }

  // 根据 dataIndex 获取值（支持嵌套路径）
  const getValueByDataIndex = (item: any, dataIndex: string | string[]): any => {
    if (!dataIndex) return ''

    // 如果 dataIndex 是数组，表示嵌套路径
    if (Array.isArray(dataIndex)) {
      return dataIndex.reduce((obj, key) => {
        return obj && obj[key] !== undefined ? obj[key] : ''
      }, item)
    }

    // 如果 dataIndex 是字符串，直接获取
    return item[dataIndex] ?? ''
  }

  // 带超时的图片转换
  const imageUrlToBase64 = (url: string): Promise<{ base64: string; aspectRatio: number }> => {
    return new Promise((resolve, reject) => {
      const timeout = setTimeout(() => {
        reject(new Error('图片加载超时'))
      }, 10000) // 10秒超时

      const img = new Image()
      img.crossOrigin = 'Anonymous'
      img.onload = () => {
        clearTimeout(timeout)
        const canvas = document.createElement('canvas')
        canvas.width = img.width
        canvas.height = img.height

        const ctx = canvas.getContext('2d')
        if (!ctx) return reject(new Error('Canvas context error'))

        ctx.drawImage(img, 0, 0)
        const base64 = canvas.toDataURL('image/jpeg')
        const aspectRatio = img.width / img.height
        resolve({ base64, aspectRatio })
      }
      img.onerror = (err) => {
        clearTimeout(timeout)
        reject(err)
      }
      img.src = url.replace('https://api.bjyz.shudaonan.cn', 'https://img.bjyz.shudaonan.cn')
    })
  }

  const handleExport = async (result: any[] = []) => {
    let hide: any = null
    let timeoutId: NodeJS.Timeout | null = null

    try {
      // 设置导出状态和超时
      setExporting(true)
      hide = message.loading('表格导出中...', 0)

      // 设置30秒超时
      timeoutId = setTimeout(() => {
        throw new Error('导出操作超时，请重试')
      }, 30000)

      // 过滤需要导出的列
      const excludeColList = [...filterColumns, 'id', 'option']
      const exportColumns = columns.filter(
        (col) =>
          !col.hideInTable && col.dataIndex && col.title && !excludeColList.includes(col.dataIndex)
      )

      // 创建工作表
      const workbook = new ExcelJS.Workbook()
      const ws = workbook.addWorksheet('Sheet1')

      // 分离普通列和图片列
      const normalColumns = exportColumns.filter((col) => !photoList.includes(col.dataIndex))
      const photoColumns = exportColumns.filter((col) => photoList.includes(col.dataIndex))

      // 根据纸张大小设置宽度
      const paperWidth = paperSizeConfig[paperSize]
      const totalColumns = 1 + normalColumns.length + photoColumns.length
      const baseColumnWidth = Math.floor(paperWidth / totalColumns)

      // 生成表头和数据 - 使用安全的 getColumnTitle 函数
      const headers = [
        '序号',
        ...(normalColumns?.map((col) => getColumnTitle(col)) || []),
        ...(photoColumns?.map((col) => getColumnTitle(col)) || []),
      ]

      const dataRows = result?.map((item, index) => [
        index + 1,
        ...normalColumns.map((col) => {
          // 特殊处理标题列
          if (col.dataIndex === 'title') {
            return getDisplayTitle(item, col)
          }

          // 优先使用 render 函数的结果
          if (col.render) {
            return getRenderValue(col, item, index)
          }

          if (col.valueEnum) {
            const value = getValueByDataIndex(item, col.dataIndex)
            const id = col.valueEnum[value]?.text?.props?.id
            return id ? intl.formatMessage({ id }) : col.valueEnum[value]
          }

          // 使用通用函数获取值（支持嵌套 dataIndex）
          return getValueByDataIndex(item, col.dataIndex)
        }),
        ...photoColumns.map(() => ''),
      ])

      ws.addRows([headers, ...dataRows])

      // 为表头添加样式
      const headerRow = ws.getRow(1)
      headerRow.height = 30
      headerRow.eachCell((cell) => {
        cell.fill = {
          type: 'pattern',
          pattern: 'solid',
          fgColor: { argb: 'cccccc' },
        }
        cell.font = {
          bold: true,
          size: 12,
          color: { argb: 'FF000000' },
        }
        cell.alignment = {
          horizontal: 'center',
          vertical: 'middle',
        }
        cell.border = {
          top: { style: 'thin' },
          left: { style: 'thin' },
          bottom: { style: 'thin' },
          right: { style: 'thin' },
        }
      })

      // 为数据行添加边框和样式
      for (let i = 0; i < result.length; i++) {
        const rowIndex = i + 2
        const row = ws.getRow(rowIndex)
        const rowData = result[i]

        // 根据行类型设置不同的背景色
        if (customConfig.rowTypeField && rowData[customConfig.rowTypeField]) {
          const rowType = rowData[customConfig.rowTypeField]
          let fillColor = 'FFFFFF' // 默认白色

          if (rowType === 'product') {
            fillColor = 'E6F3FF' // 产品行使用浅蓝色背景
          } else if (rowType === 'operator') {
            fillColor = 'F0F0F0' // 运营商行使用浅灰色背景
          }

          row.eachCell((cell) => {
            cell.fill = {
              type: 'pattern',
              pattern: 'solid',
              fgColor: { argb: fillColor },
            }
          })
        }

        for (let colIndex = 1; colIndex <= headers.length; colIndex++) {
          const cell = row.getCell(colIndex)
          cell.border = {
            top: { style: 'thin' },
            left: { style: 'thin' },
            bottom: { style: 'thin' },
            right: { style: 'thin' },
          }

          if (colIndex === 1) {
            cell.alignment = {
              horizontal: 'center',
              vertical: 'middle',
            }
          } else if (colIndex > 1 + normalColumns.length) {
            cell.alignment = {
              horizontal: 'center',
              vertical: 'middle',
            }
          } else {
            cell.alignment = {
              horizontal: 'left',
              vertical: 'middle',
              wrapText: true,
            }
          }
        }
      }

      // 确保所有单元格都有边框
      for (let rowIndex = 1; rowIndex <= result.length + 1; rowIndex++) {
        const row = ws.getRow(rowIndex)
        for (let colIndex = 1; colIndex <= headers.length; colIndex++) {
          const cell = row.getCell(colIndex)
          if (!cell.border) {
            cell.border = {
              top: { style: 'thin' },
              left: { style: 'thin' },
              bottom: { style: 'thin' },
              right: { style: 'thin' },
            }
          }
        }
      }

      // 插入图片
      for (let i = 0; i < result.length; i++) {
        const rowIndex = i + 2

        for (const photoField of photoList) {
          const photoColIndex = photoColumns.findIndex((col) => col.dataIndex === photoField)
          if (photoColIndex < 0) continue

          const photoUrl = result[i][photoField]
          if (photoUrl) {
            try {
              const { base64, aspectRatio } = await imageUrlToBase64(photoUrl)
              const imageId = workbook.addImage({
                base64,
                extension: 'jpeg',
              })

              const fixedHeight = 80
              const calculatedWidth = fixedHeight * aspectRatio
              const actualColIndex = 1 + normalColumns.length + photoColIndex

              ws.addImage(imageId, {
                tl: { col: actualColIndex + 0.2, row: rowIndex - 1 + 0.2 },
                ext: { width: calculatedWidth - 10, height: fixedHeight - 10 },
              })
              ws.getRow(rowIndex).height = 70
            } catch (err) {
              console.warn(`图片插入失败: ${photoUrl}`, err)
            }
          }
        }
      }

      // 设置列宽
      ws.columns.forEach((col, colIdx) => {
        if (colIdx === 0) {
          col.width = 8
          return
        }

        const normalColIndex = colIdx - 1
        if (normalColIndex < normalColumns.length) {
          const header = headers[colIdx] ?? ''
          const headerLen = header.split('').reduce((sum: number, ch: string) => {
            return sum + (ch.charCodeAt(0) > 255 ? 2 : 1)
          }, 0)
          const calculatedWidth = Math.max(headerLen, 8)
          col.width = Math.min(calculatedWidth + 2, baseColumnWidth)
        } else {
          col.width = Math.min(40, baseColumnWidth)
        }
      })

      // 生成文件并下载
      const buffer = await workbook.xlsx.writeBuffer()
      const blob = new Blob([buffer], {
        type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
      })
      saveAs(blob, `${fileName}.xlsx`)

      // 清除超时
      if (timeoutId) clearTimeout(timeoutId)
      hide()
      setExporting(false)
      message.success('表格导出成功')
    } catch (error) {
      // 清理超时和加载状态
      if (timeoutId) clearTimeout(timeoutId)
      if (hide) hide()
      setExporting(false)

      console.error('导出失败:', error)
      message.error(`导出失败: ${error instanceof Error ? error.message : '未知错误'}`)
    }
  }

  return (
    <Button
      type='primary'
      icon={<DownloadOutlined />}
      loading={exporting}
      disabled={exporting}
      onClick={async () => {
        if (onFinish) {
          try {
            const result = (await onFinish()) || []
            if (result.length === 0) {
              message.warning('没有可导出的数据')
              return
            }
            await handleExport(result)
          } catch (error) {
            console.error('获取导出数据失败:', error)
            message.error('获取导出数据失败，请重试')
            setExporting(false)
          }
        }
      }}
    >
      导出Excel
    </Button>
  )
}

export default ExportExcelButton
